import { Order, OrderNotificationType } from 'API';
import { useToast } from 'providers/ToastProvider';
import { Fragment, useEffect, useRef } from 'react';
import { ToastNotificationIds, ToastNotificationType } from 'shared/enums';
import { checkPriceOrTaxBeingCalculated, getPricingMessage } from 'shared/helpers/util.helper';
import { useOrderNotificationAvailable } from 'shared/hooks/useOrderNotificationAvailable';
import { AlertMessage } from '../Alert/AlertMessage';

const ToastNotificationMap = {
  [OrderNotificationType.TaxCalculated]: {
    remove: [
      ToastNotificationIds.PRICE_CALCULATION,
      ToastNotificationIds.TAX_CALCULATION,
      ToastNotificationIds.COMPLETED_PRICE_CALCULATION,
    ],
    message: "Case's pricing calculation is completed",
    id: ToastNotificationIds.COMPLETED_TAX_CALCULATION,
  },
};

/**
 * Props for the OrderProcessingNotification component.
 */
interface OrderProcessingNotificationProps {
  /** Metadata of the order. */
  orderMetaData: Order['metadata'];
  /** Order number. */
  orderNumber: string;
  /** Callback function to refresh the order. */
  onRefresh?: () => void;
  /** Boolean indicating whether to hide the alert. */
  isAlertMessageHidden?: boolean;
  /** Indicates where we are using a toast message instead of an HTML component */
  isUsingToast?: boolean;
  /** Boolean indicating whether to skip the calculation completed toast in case of bundle orders */
  isUserNotifiedOfPricingCalculationCompletion?: boolean;
}

/**
 * Component to display processing notification for an order.
 *
 * @param orderMetaData - Metadata of the order.
 * @param orderNumber - Order number.
 * @param onRefresh - Callback function to refresh the order.
 * @param isUsingToast - Indicates where we are using a toast message instead of an HTML component.
 * @returns JSX.Element - The rendered component.
 */
export const OrderProcessingNotification: React.FC<OrderProcessingNotificationProps> = ({
  orderMetaData,
  orderNumber,
  onRefresh,
  isUsingToast = false,
  isUserNotifiedOfPricingCalculationCompletion = true,
}) => {
  const { orderNotificationEvent, resetNotificationEvent } = useOrderNotificationAvailable(orderNumber);
  const isOrderProcessing = checkPriceOrTaxBeingCalculated(orderMetaData);
  const toast = useToast();
  const onRefreshRef = useRef(onRefresh);
  const isPricingBeingCalculated = orderMetaData?.isPricingBeingCalculated || false;
  const isTaxBeingCalculated = orderMetaData?.isTaxBeingCalculated || false;

  const messageToShow = getPricingMessage(isPricingBeingCalculated, isTaxBeingCalculated);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!orderNotificationEvent || orderNotificationEvent !== OrderNotificationType.TaxCalculated) {
      // it was confirmed with Service team that there is no need to listen to the pricing calculation event,
      // since the `TaxCalculated` complete event will always be the last event the UI receives,
      // to notify it that the order pricing calculation step is complete.
      // refer https://glidewell.atlassian.net/browse/LMS1-8993 for more details
      return;
    }
    const notification = ToastNotificationMap[orderNotificationEvent];
    if (notification && isUserNotifiedOfPricingCalculationCompletion) {
      // Remove existing toasts related to the event
      notification.remove.forEach(toast.remove);
      // Show success toast notification
      toast.notify(notification.message, ToastNotificationType.Success, {
        duration: 2000,
        id: notification.id,
      });
    }

    resetNotificationEvent();
    onRefreshRef.current?.();
  }, [orderNotificationEvent, toast, resetNotificationEvent, isUserNotifiedOfPricingCalculationCompletion]);

  useEffect(() => {
    if ((isPricingBeingCalculated || isTaxBeingCalculated) && messageToShow && isUsingToast) {
      toast.remove(ToastNotificationIds.PRICE_CALCULATION);
      toast.remove(ToastNotificationIds.TAX_CALCULATION);
      // Show until a message comes back.
      toast.notify(messageToShow, ToastNotificationType.Info, {
        duration: Infinity,
        id: isTaxBeingCalculated ? ToastNotificationIds.TAX_CALCULATION : ToastNotificationIds.PRICE_CALCULATION,
      });
    }
  }, [isPricingBeingCalculated, isTaxBeingCalculated, isUsingToast, messageToShow, toast]);

  return isOrderProcessing && !isUsingToast ? (
    <AlertMessage message={messageToShow} type={ToastNotificationType.Info} className="w-full" />
  ) : (
    <Fragment></Fragment>
  );
};
